import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import type { RoutableProps } from 'preact-router';
import {
  Share2,
  Copy,
  Check,
  Home,
  BookOpen,
  Sparkles,
  ArrowLeft,
  Star,
} from 'lucide-preact';
import { HEROES, SETTINGS, STORY_TYPES } from '../lib/constants';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { StoryReader } from '../components/StoryReader';
import { getStoryById, saveStory } from '../lib/storage';
import { getSharedStory, shareStory } from '../lib/api';
import type { Story } from '../lib/types';

interface ReadProps extends RoutableProps {
  id?: string;
}

export function Read({ id }: ReadProps) {
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('Story not found');
      setLoading(false);
      return;
    }

    const localStory = getStoryById(id);
    if (localStory) {
      setStory(localStory);
      setLoading(false);
      return;
    }

    getSharedStory(id)
      .then((sharedStory) => {
        setStory(sharedStory);
        saveStory(sharedStory);
      })
      .catch(() => {
        setError('Story not found');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const handleShare = async () => {
    if (!story) return;
    setSharing(true);
    try {
      const sharedId = await shareStory(story);
      const url = `${window.location.origin}/read/${sharedId}`;
      setShareUrl(url);
    } catch {
      setError('Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-warm flex items-center justify-center">
        <LoadingSpinner message="Opening your story..." />
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen gradient-warm flex items-center justify-center">
        <div className="text-center animate-in">
          <div className="w-20 h-20 rounded-full bg-parchment border-ornate flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-10 h-10 text-ink/30" strokeWidth={1.25} />
          </div>
          <h1 className="text-3xl font-display font-medium text-ink mb-4">Story Not Found</h1>
          <p className="text-ink/60 mb-8">{error || 'This story may have been moved or deleted.'}</p>
          <button
            onClick={() => route('/')}
            className="px-6 py-3 bg-rust text-cream rounded-2xl font-medium shadow-story
                     hover:shadow-story-hover transition-all cursor-pointer"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-cream/80 backdrop-blur-md border-b border-gold/10">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => route('/')}
              className="flex items-center gap-2 text-ink/50 hover:text-ink transition-colors cursor-pointer"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Home</span>
            </button>

            {/* Share button */}
            <button
              onClick={handleShare}
              disabled={sharing}
              className="flex items-center gap-2 px-4 py-2 text-ink/50 hover:text-ink transition-colors cursor-pointer"
            >
              <Share2 size={18} />
              <span className="hidden sm:inline">{sharing ? 'Sharing...' : 'Share'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Story Content */}
      <main className="container-custom py-12 sm:py-16">
        <article className="max-w-2xl mx-auto">
          {/* Premium Title Section */}
          <header className="text-center mb-12 animate-in" style={{ animationDelay: '0.1s' }}>
            {/* Story metadata badges */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {(() => {
                const heroData = HEROES.find(h => h.value === story.choices.animal);
                const settingData = SETTINGS.find(s => s.value === story.choices.setting);
                const typeData = STORY_TYPES.find(t => t.value === story.choices.storyType);
                return (
                  <>
                    {heroData && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-parchment text-ink/60 border border-gold/20">
                        <heroData.icon size={12} />
                        {heroData.label}
                      </span>
                    )}
                    {typeData && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-parchment text-ink/60 border border-gold/20">
                        <typeData.icon size={12} />
                        {typeData.label}
                      </span>
                    )}
                    {settingData && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-parchment text-ink/60 border border-gold/20">
                        <settingData.icon size={12} />
                        {settingData.label}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Decorative star cluster */}
            <div className="inline-flex items-center gap-3 text-gold/60 mb-4">
              <Star size={10} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={10} fill="currentColor" />
            </div>

            {/* Hero subtitle */}
            <p className="text-lg sm:text-xl text-ink/50 font-display italic mb-4">
              A tale for {story.choices.childName}
            </p>

            {/* Main title with decorative treatment */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-medium text-ink leading-tight mb-4">
              {story.title}
            </h1>

            {/* Bottom decorative line */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <div className="w-16 h-px bg-gradient-to-r from-transparent to-gold/40" />
              <Sparkles size={16} className="text-gold/50" />
              <div className="w-16 h-px bg-gradient-to-l from-transparent to-gold/40" />
            </div>
          </header>

          {/* Story body - with drop cap and elegant typography */}
          <div
            className="relative bg-cream rounded-3xl p-8 sm:p-12 lg:p-16 border-ornate animate-in"
            style={{ animationDelay: '0.2s' }}
          >

            {/* Decorative top ornament */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-12 h-12 rounded-full bg-cream border-ornate flex items-center justify-center">
                <BookOpen size={20} className="text-gold" strokeWidth={1.5} />
              </div>
            </div>

            <StoryReader story={story} />

            {/* Premium "The End" ornament */}
            <div className="mt-16 text-center">
              <div className="inline-flex flex-col items-center gap-4">
                {/* Decorative flourish */}
                <div className="flex items-center gap-4">
                  <div className="w-8 h-px bg-gold/40" />
                  <Star size={8} className="text-gold/50" fill="currentColor" />
                  <div className="w-16 h-px bg-gold/50" />
                  <Star size={8} className="text-gold/50" fill="currentColor" />
                  <div className="w-8 h-px bg-gold/40" />
                </div>

                {/* The End text */}
                <div className="relative">
                  <span className="font-display text-2xl text-ink/40 tracking-wide">The End</span>
                </div>

                {/* Bottom flourish */}
                <div className="flex items-center gap-3">
                  <div className="w-6 h-px bg-gold/30" />
                  <Star size={6} className="text-gold/40" fill="currentColor" />
                  <div className="w-6 h-px bg-gold/30" />
                </div>
              </div>
            </div>
          </div>

          {/* Share URL display */}
          {shareUrl && (
            <div
              className="mt-8 p-6 bg-parchment rounded-2xl border-ornate animate-in"
              style={{ animationDelay: '0.3s' }}
            >
              <p className="text-sm text-ink/60 mb-3 text-center">Share this story with others:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-4 py-3 text-sm bg-cream border border-gold/20 rounded-xl text-ink/70"
                />
                <button
                  onClick={handleCopy}
                  className="px-4 py-3 bg-rust text-cream rounded-xl font-medium flex items-center gap-2
                           hover:shadow-story transition-all cursor-pointer"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div
            className="mt-12 flex flex-col sm:flex-row gap-4 justify-center animate-in"
            style={{ animationDelay: '0.4s' }}
          >
            <button
              onClick={() => route('/create')}
              className="group px-6 py-4 bg-rust text-cream rounded-2xl font-medium
                       flex items-center justify-center gap-3 shadow-story
                       hover:shadow-story-hover hover:-translate-y-0.5 transition-all cursor-pointer"
            >
              <Sparkles size={20} className="group-hover:rotate-12 transition-transform" />
              Create Another Story
            </button>
            <button
              onClick={() => route('/library')}
              className="px-6 py-4 bg-parchment text-ink rounded-2xl font-medium
                       flex items-center justify-center gap-3 border-ornate
                       hover:shadow-story transition-all cursor-pointer"
            >
              <BookOpen size={20} />
              My Stories
            </button>
            <button
              onClick={() => route('/')}
              className="px-6 py-4 text-ink/50 hover:text-ink rounded-2xl font-medium
                       flex items-center justify-center gap-2 transition-colors cursor-pointer"
            >
              <Home size={20} />
              Home
            </button>
          </div>
        </article>
      </main>
    </div>
  );
}
